import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

const Inbox = ({ selectedAsset, onBack }) => {
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [replyText, setReplyText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [syncNotice, setSyncNotice] = useState(null)
  const [autoSyncKey, setAutoSyncKey] = useState(null)
  const [flagging, setFlagging] = useState(false)
  const [liveEnabled, setLiveEnabled] = useState(false)
  const [liveStatus, setLiveStatus] = useState('disconnected') // disconnected|connecting|connected|error
  const [liveLastEventAt, setLiveLastEventAt] = useState(null)
  const selectedConversationRef = useRef(null)

  const defaultBackendUrl =
    typeof window !== 'undefined' ? window.location.origin : ''
  const backendUrl = process.env.REACT_APP_BACKEND_URL || defaultBackendUrl

  const getMessageText = (message) => {
    const text = (message?.text || '').trim()
    if (text) return text
    if (message?.attachments) {
      return '[Attachment]'
    }
    return '[Non-text message]'
  }

  const getSenderLabel = (message, conversation) => {
    const fromId = message?.from_id || message?.from?.id || null
    if (fromId && conversation?.fb_page_id && fromId === conversation.fb_page_id) {
      return conversation.page_name || 'Page'
    }
    if (fromId && conversation?.ig_id && fromId === conversation.ig_id) {
      return selectedAsset?.label || 'Instagram account'
    }
    return message?.from_name || 'User'
  }

  const getConversationLabel = (conversation) => {
    const names = Array.isArray(conversation?.participant_names)
      ? conversation.participant_names.filter(Boolean)
      : []
    const ownNames = new Set(
      [
        conversation?.page_name,
        conversation?.ig_username,
        selectedAsset?.label,
      ].filter(Boolean),
    )

    const nameCandidate = names.find((name) => name && !ownNames.has(name))
    if (nameCandidate) return nameCandidate

    return 'User'
  }

  const loadInbox = async (asset, { preserveNotice = false } = {}) => {
    try {
      setLoading(true)
      setSelectedConversation(null)
      setMessages([])
      if (!preserveNotice) {
        setSyncNotice(null)
      }
      const current = asset || selectedAsset
      if (!current) {
        setConversations([])
        return []
      }
      const response = await axios.get(`${backendUrl}/inbox`, {
        withCredentials: true,
        params: {
          page_id: current.id,
          platform: current.type,
          connection_source: current.connectionSource || undefined,
          limit: 50,
        },
      })
      const items = response.data.conversations || []
      setConversations(items)
      return items
    } catch (err) {
      setError('Failed to load inbox.')
      return []
    } finally {
      setLoading(false)
    }
  }

  const loadThread = async (conversationId) => {
    try {
      setLoading(true)
      let response = await axios.get(
        `${backendUrl}/threads/${conversationId}`,
        {
          withCredentials: true,
        },
      )

      if (
        !response.data.replied &&
        Array.isArray(response.data.messages) &&
        response.data.messages.length === 0
      ) {
        try {
          await axios.post(
            `${backendUrl}/threads/${conversationId}/sync`,
            { limit: 50, mode: 'refresh' },
            { withCredentials: true },
          )
          response = await axios.get(`${backendUrl}/threads/${conversationId}`, {
            withCredentials: true,
          })
        } catch (error) {
          // keep initial response if sync fails
        }
      }

      setSelectedConversation(response.data.conversation)
      setMessages(response.data.messages || [])
      if (response.data.replied) {
        setSyncNotice('Conversation replied to. Not monitored.')
      } else {
        setSyncNotice(null)
      }
    } catch (err) {
      setError('Failed to load conversation.')
    } finally {
      setLoading(false)
    }
  }

  const refreshInboxForLive = async (asset) => {
    const current = asset || selectedAsset
    if (!current) return
    const response = await axios.get(`${backendUrl}/inbox`, {
      withCredentials: true,
      params: {
        page_id: current.id,
        platform: current.type,
        connection_source: current.connectionSource || undefined,
        limit: 50,
      },
    })
    setConversations(response.data.conversations || [])
  }

  const refreshSelectedThreadForLive = async () => {
    const current = selectedConversationRef.current
    if (!current?.id) return
    await axios.post(
      `${backendUrl}/threads/${current.id}/sync`,
      { limit: 50, mode: 'refresh' },
      { withCredentials: true },
    )
    const response = await axios.get(`${backendUrl}/threads/${current.id}`, {
      withCredentials: true,
    })
    setSelectedConversation(response.data.conversation)
    setMessages(response.data.messages || [])
  }

  const subscribeWebhooksForAsset = async (asset) => {
    const current = asset || selectedAsset
    if (!current?.id) return
    if (
      current.type === 'instagram' &&
      current.connectionSource === 'instagram_login'
    ) {
      return
    }
    await axios.post(
      `${backendUrl}/pages/${current.id}/subscribe-webhooks`,
      {},
      { withCredentials: true },
    )
  }

  useEffect(() => {
    selectedConversationRef.current = selectedConversation
  }, [selectedConversation])

  useEffect(() => {
    setLiveEnabled(false)
    setLiveStatus('disconnected')
    setLiveLastEventAt(null)
  }, [selectedAsset?.id, selectedAsset?.type])

  useEffect(() => {
    if (!selectedAsset?.id || !liveEnabled) return

    const isInstagramLoginLive =
      selectedAsset.type === 'instagram' &&
      selectedAsset.connectionSource === 'instagram_login'

    if (isInstagramLoginLive) {
      let cancelled = false
      let intervalId
      let inFlight = false

      const poll = async () => {
        if (cancelled || inFlight) return
        inFlight = true
        try {
          await axios.post(
            `${backendUrl}/sync/conversations`,
            {
              page_id: selectedAsset.id,
              platform: selectedAsset.type,
              limit: 20,
              mode: 'refresh',
            },
            { withCredentials: true },
          )
          await refreshInboxForLive(selectedAsset)
          await refreshSelectedThreadForLive()
          setLiveStatus('connected')
          setLiveLastEventAt(new Date().toISOString())
        } catch (error) {
          setLiveStatus('error')
        } finally {
          inFlight = false
        }
      }

      setLiveStatus('connecting')
      poll()
      intervalId = setInterval(poll, 10000)

      return () => {
        cancelled = true
        clearInterval(intervalId)
      }
    }

    let es
    let cancelled = false
    const start = async () => {
      try {
        setLiveStatus('connecting')
        await subscribeWebhooksForAsset(selectedAsset)
        if (cancelled) return

        es = new EventSource(`${backendUrl}/pages/${selectedAsset.id}/events`, {
          withCredentials: true,
        })

        es.addEventListener('ready', () => {
          setLiveStatus('connected')
        })

        es.addEventListener('message', (event) => {
          try {
            const data = JSON.parse(event.data || '{}')
            setLiveLastEventAt(data.received_at || new Date().toISOString())

            const conversationDbId = data.conversation_db_id
            const message = data.message
            if (!conversationDbId || !message) return

            setConversations((prev) => {
              const idx = prev.findIndex((conversation) => conversation.id === conversationDbId)
              if (idx === -1) return prev
              const current = prev[idx]
              const snippet =
                typeof message.text === 'string' && message.text.trim()
                  ? message.text.trim().slice(0, 120)
                  : current.snippet
              const updatedTime =
                message.created_time || message.created_at || current.updated_time
              const updated = {
                ...current,
                snippet,
                updated_time: updatedTime,
                last_message_time: updatedTime,
              }
              const next = [...prev]
              next.splice(idx, 1)
              next.unshift(updated)
              return next
            })

            if (selectedConversationRef.current?.id === conversationDbId) {
              setMessages((prev) => [...prev, message])
            }
          } catch (error) {
            // ignore
          }
        })

        es.addEventListener('error', () => {
          setLiveStatus('error')
        })
      } catch (error) {
        setLiveStatus('error')
      }
    }

    start()

    return () => {
      cancelled = true
      try {
        es?.close()
      } catch (error) {
        // ignore
      }
    }
  }, [backendUrl, liveEnabled, selectedAsset?.id])

  const handleSyncMoreConversations = async () => {
    if (!selectedAsset) return
    try {
      setLoading(true)
      const response = await axios.post(
        `${backendUrl}/sync/conversations`,
        {
          page_id: selectedAsset.id,
          platform: selectedAsset.type,
          limit: 20,
          mode: 'load_more',
        },
        { withCredentials: true },
      )
      if ((response.data?.synced || 0) === 0) {
        await loadInbox(selectedAsset, { preserveNotice: true })
        setSyncNotice('No more conversations found.')
      } else {
        setSyncNotice(null)
        await loadInbox(selectedAsset, { preserveNotice: true })
      }
    } catch (err) {
      setError('Failed to sync conversations.')
    } finally {
      setLoading(false)
    }
  }

  const handleSyncMoreMessages = async () => {
    if (!selectedConversation) return
    try {
      setLoading(true)
      const syncResponse = await axios.post(
        `${backendUrl}/threads/${selectedConversation.id}/sync`,
        { limit: 50, mode: 'load_more' },
        { withCredentials: true },
      )
      if (syncResponse.data?.replied) {
        setMessages([])
        setSyncNotice('Conversation replied to. Not monitored.')
        return
      }
      if ((syncResponse.data?.synced || 0) === 0) {
        setSyncNotice('No more messages found.')
      } else {
        setSyncNotice(null)
      }
      await loadThread(selectedConversation.id)
    } catch (err) {
      setError('Failed to sync messages.')
    } finally {
      setLoading(false)
    }
  }

  const handleReply = async (event) => {
    event.preventDefault()
    if (!selectedConversation || !replyText.trim()) return

    try {
      setLoading(true)
      await axios.post(
        `${backendUrl}/threads/${selectedConversation.id}/reply`,
        { text: replyText },
        { withCredentials: true },
      )
      setReplyText('')
      await loadThread(selectedConversation.id)
    } catch (err) {
      setError('Failed to send reply.')
    } finally {
      setLoading(false)
    }
  }

  const handleFlagConversation = async () => {
    if (!selectedConversation) return
    try {
      setFlagging(true)
      const endpoint = selectedConversation.is_flagged
        ? 'unflag'
        : 'flag'
      await axios.post(
        `${backendUrl}/threads/${selectedConversation.id}/${endpoint}`,
        {},
        { withCredentials: true },
      )
      await loadThread(selectedConversation.id)
    } catch (err) {
      setError('Failed to flag conversation.')
    } finally {
      setFlagging(false)
    }
  }

  useEffect(() => {
    if (!selectedAsset) return
    const currentKey = `${selectedAsset.type}:${selectedAsset.id}`
    let cancelled = false

    const run = async () => {
      await loadInbox(selectedAsset)
      if (cancelled) return
      if (
        selectedAsset.type === 'instagram' &&
        autoSyncKey !== currentKey
      ) {
        setAutoSyncKey(currentKey)
        try {
          await axios.post(
            `${backendUrl}/sync/conversations`,
            {
              page_id: selectedAsset.id,
              platform: selectedAsset.type,
              limit: 20,
              mode: 'refresh',
            },
            { withCredentials: true },
          )
          await loadInbox(selectedAsset, { preserveNotice: true })
        } catch (err) {
          setError('Failed to sync Instagram conversations.')
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [selectedAsset?.id, selectedAsset?.type, autoSyncKey])

  return (
    <Card elevation={3} style={{ marginTop: '24px' }}>
      <CardContent>
        <Button variant="text" onClick={onBack} style={{ marginBottom: '8px' }}>
          Back
        </Button>
        <Typography variant="h6" gutterBottom>
          Inbox
        </Typography>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '8px',
          }}
        >
          <Typography variant="body2">Live updates</Typography>
          <Switch
            checked={liveEnabled}
            onChange={(event) => setLiveEnabled(event.target.checked)}
            disabled={!selectedAsset}
          />
        </Box>
        <Typography variant="caption" color="textSecondary" display="block">
          Status: {liveStatus}
          {liveLastEventAt ? ` • last webhook: ${liveLastEventAt}` : ''}
        </Typography>
        <Divider style={{ marginBottom: '16px' }} />

        {error && (
          <Typography color="error" variant="body2" gutterBottom>
            {error}
          </Typography>
        )}
        {syncNotice && (
          <Typography color="textSecondary" variant="body2" gutterBottom>
            {syncNotice}
          </Typography>
        )}

        <Box className="inbox-layout">
          <Box className="inbox-list">
            <Typography variant="subtitle2" gutterBottom>
              Conversations
            </Typography>
            {loading && conversations.length === 0 ? (
              <Typography variant="body2">Loading...</Typography>
            ) : (
              <List dense>
                {conversations.map((conversation) => (
                  <ListItemButton
                    key={conversation.id}
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => loadThread(conversation.id)}
                  >
                    <ListItemText
                      primary={
                        conversation.is_replied_to
                          ? `${getConversationLabel(conversation)} (Not monitored)`
                          : getConversationLabel(conversation)
                      }
                      secondary={
                        conversation.is_replied_to
                          ? 'Conversation replied to. Not monitored.'
                          : conversation.snippet
                            ? conversation.snippet
                            : conversation.updated_time
                              ? `Updated: ${conversation.updated_time}`
                              : conversation.last_checked
                                ? 'Checked'
                                : 'Not checked yet'
                      }
                    />
                  </ListItemButton>
                ))}
                {conversations.length === 0 && (
                  <Typography variant="body2">
                    No conversations yet.
                  </Typography>
                )}
              </List>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={handleSyncMoreConversations}
              disabled={!selectedAsset || loading}
              style={{ marginTop: '8px' }}
            >
              Load more conversations
            </Button>
          </Box>

          <Box className="inbox-thread">
            {selectedConversation ? (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Thread
                </Typography>
                <Box className="inbox-messages">
                  {selectedConversation.is_replied_to ? (
                    <Typography variant="body2">
                      Conversation replied to. Not monitored.
                    </Typography>
                  ) : (
                    <>
                      {selectedConversation.is_flagged && (
                        <Typography variant="body2" color="textSecondary">
                          Flagged for review.
                        </Typography>
                      )}
                      {messages.map((message) => (
                        <Box
                          key={message.id || message.message_id}
                          className={
                            message.is_outgoing
                              ? 'inbox-message outgoing'
                              : 'inbox-message incoming'
                          }
                        >
                          <Typography variant="caption" color="textSecondary">
                            {getSenderLabel(message, selectedConversation)}
                          </Typography>
                          <Typography variant="body2">
                            {getMessageText(message)}
                          </Typography>
                        </Box>
                      ))}
                      {messages.length === 0 && (
                        <Typography variant="body2">
                          No messages yet.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleFlagConversation}
                  disabled={!selectedConversation || flagging || loading}
                  style={{ marginBottom: '8px' }}
                >
                  {selectedConversation?.is_flagged
                    ? 'Unflag conversation'
                    : 'Flag conversation'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSyncMoreMessages}
                  disabled={loading || selectedConversation.is_replied_to}
                  style={{ marginBottom: '8px' }}
                >
                  Load messages
                </Button>

                <Box component="form" onSubmit={handleReply}>
                  <TextField
                    label="Reply"
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    margin="dense"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={
                      loading || !replyText.trim() || selectedConversation.is_replied_to
                    }
                  >
                    Send
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body2">
                Select a conversation to view messages.
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default Inbox
